import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, ButtonBase, Collapse, Stack, Typography,
} from '@mui/material';
import {
  CalendarMonthOutlined, ChevronRightRounded, DescriptionOutlined, ExpandMoreRounded,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { collection, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import colors from '../../assets/theme/colors';
import { canEditChildProfile } from '../../constants/roles';
import { useRole } from '../../contexts/RoleContext';
import { db } from '../../services/firebase';
import PreparationLifecycleMenu from './PreparationLifecycleMenu';
import {
  archivePreparationDraft, restorePreparationDraft,
} from './preparationDraftService';
import {
  getArchivedPreparationLabel, isFinishedPreparation, timestampValue,
} from './preparationArchiveModel';

const STATUS_COPY = {
  plan_review: 'Plan ready to review',
  preparing: 'Preparing answers',
  updating: 'Updating preparation',
  ready: 'Ready to review',
  ready_with_attention: 'Ready to review · document needs attention',
  needs_attention: 'Needs attention',
};

const displayDate = (value) => {
  const timestamp = timestampValue(value);
  if (!timestamp) return '';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(timestamp));
};

const sessionKey = (childId) => `lifelog:preparations-expanded:${childId}`;

const CustomPreparationStatusList = ({ childId }) => {
  const navigate = useNavigate();
  const { getUserRoleForChild } = useRole();
  const [drafts, setDrafts] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [lifecycleError, setLifecycleError] = useState('');
  const canManage = canEditChildProfile(getUserRoleForChild?.(childId));

  useEffect(() => {
    if (!childId || !canManage) {
      setDrafts([]);
      return undefined;
    }
    return onSnapshot(collection(db, 'children', childId, 'preparationDrafts'), (snapshot) => {
      setDrafts(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    }, () => setDrafts([]));
  }, [canManage, childId]);

  const preparations = useMemo(() => drafts
    .filter((item) => item.templateId === 'custom_prepare' && STATUS_COPY[item.preparationStatus])
    .sort((left, right) => timestampValue(right.updatedAt) - timestampValue(left.updatedAt)), [drafts]);
  const unarchived = useMemo(() => preparations.filter((item) => !item.isArchived), [preparations]);
  const active = useMemo(() => unarchived.filter((item) => !isFinishedPreparation(item)), [unarchived]);
  const past = useMemo(() => unarchived.filter(isFinishedPreparation), [unarchived]);
  const archived = useMemo(() => preparations.filter((item) => item.isArchived)
    .sort((left, right) => timestampValue(right.archivedAt) - timestampValue(left.archivedAt)), [preparations]);

  useEffect(() => {
    if (!preparations.length || expanded !== null) return;
    const saved = window.sessionStorage.getItem(sessionKey(childId));
    setExpanded(saved === null ? active.length <= 3 : saved === 'true');
  }, [active.length, childId, expanded, preparations.length]);

  if (!canManage || !preparations.length) return null;

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    window.sessionStorage.setItem(sessionKey(childId), String(next));
  };

  const openPreparation = (item) => {
    if (item.preparationStatus === 'plan_review' && !item.isArchived) {
      navigate(`/prepare?draftId=${encodeURIComponent(item.id)}`, {
        state: { selectedChildId: childId },
      });
      return;
    }
    navigate(`/children/${childId}/journeys/custom/${item.id}`);
  };

  const changeArchiveState = async (item, nextArchived) => {
    setLifecycleError('');
    try {
      const update = nextArchived
        ? await archivePreparationDraft({ childId, draftId: item.id })
        : await restorePreparationDraft({ childId, draftId: item.id });
      setDrafts((current) => current.map((draftItem) => (
        draftItem.id === item.id ? { ...draftItem, ...update } : draftItem
      )));
      return true;
    } catch (archiveError) {
      setLifecycleError(archiveError?.message || 'This preparation could not be updated.');
      return false;
    }
  };

  const renderPreparation = (item, { finished = false, archivedItem = false } = {}) => (
    <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', borderRadius: '18px', bgcolor: '#FFFFFF', border: `1px solid ${alpha('#765CCB', 0.18)}` }}>
      <ButtonBase onClick={() => openPreparation(item)} sx={{ flex: 1, minWidth: 0, p: 1.15, borderRadius: '18px', justifyContent: 'flex-start', textAlign: 'left' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', minWidth: 0 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '14px', bgcolor: alpha('#765CCB', 0.08), display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <DescriptionOutlined sx={{ color: '#765CCB' }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 750, color: colors.semantic.ink }}>
            {item.journeyPlan?.title || item.customPlanProposal?.title || 'Preparation'}
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: item.preparationStatus === 'needs_attention' ? '#8F5B1F' : alpha(colors.semantic.ink, 0.62) }}>
            {archivedItem ? getArchivedPreparationLabel(item) : (finished ? 'Finished' : STATUS_COPY[item.preparationStatus])}
            {displayDate(archivedItem ? item.archivedAt : (finished ? item.latestFinalizedAt : item.updatedAt))
              ? ` · ${archivedItem ? 'Archived ' : (finished ? '' : 'Updated ')}${displayDate(archivedItem ? item.archivedAt : (finished ? item.latestFinalizedAt : item.updatedAt))}`
              : ''}
          </Typography>
        </Box>
        </Box>
      </ButtonBase>
      <PreparationLifecycleMenu
        draft={item}
        canManage={canManage}
        onArchive={() => changeArchiveState(item, true)}
        onRestore={() => changeArchiveState(item, false)}
      />
      <ChevronRightRounded sx={{ mr: 0.8, color: alpha('#765CCB', 0.7) }} />
    </Box>
  );

  return (
    <Stack spacing={0.7} sx={{ mb: 1.2 }}>
      <ButtonBase
        onClick={toggleExpanded}
        aria-expanded={Boolean(expanded)}
        aria-controls="custom-preparations-list"
        sx={{ px: 0.3, py: 0.35, borderRadius: '10px', justifyContent: 'space-between' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
          <CalendarMonthOutlined sx={{ fontSize: 18, color: '#765CCB' }} />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: alpha(colors.semantic.ink, 0.68) }}>
            Your preparations · {unarchived.length}
          </Typography>
        </Box>
        <ExpandMoreRounded sx={{ color: '#765CCB', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }} />
      </ButtonBase>
      <Collapse id="custom-preparations-list" in={Boolean(expanded)} timeout="auto">
        <Stack spacing={0.75}>
          {lifecycleError ? <Alert severity="error">{lifecycleError}</Alert> : null}
          {active.map((item) => renderPreparation(item))}
          {past.length ? (
            <>
              <ButtonBase onClick={() => setShowPast((current) => !current)} sx={{ alignSelf: 'flex-start', px: 0.5, py: 0.45, borderRadius: '8px' }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 720, color: '#765CCB' }}>
                  {showPast ? 'Hide past preparations' : `View past preparations (${past.length})`}
                </Typography>
              </ButtonBase>
              <Collapse in={showPast} timeout="auto">
                <Stack spacing={0.75}>{past.map((item) => renderPreparation(item, { finished: true }))}</Stack>
              </Collapse>
            </>
          ) : null}
          {archived.length ? (
            <>
              <ButtonBase onClick={() => setShowArchived((current) => !current)} sx={{ alignSelf: 'flex-start', px: 0.5, py: 0.45, borderRadius: '8px' }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 720, color: '#765CCB' }}>
                  {showArchived ? 'Hide archived preparations' : `Archived preparations (${archived.length})`}
                </Typography>
              </ButtonBase>
              <Collapse in={showArchived} timeout="auto">
                <Stack spacing={0.75}>{archived.map((item) => renderPreparation(item, { archivedItem: true }))}</Stack>
              </Collapse>
            </>
          ) : null}
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default CustomPreparationStatusList;
